import { useMemo, useState } from 'react';
import OneTimeEventModal from './OneTimeEventModal';

const formatContacts = (event) => {
  const names = (event.eventContacts || [])
    .map((eventContact) => eventContact.contact?.name || eventContact.name)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  if (names.length === 0) return '-';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]}, ${names[1]}`;
  return `${names[0]}, ${names[1]} +${names.length - 2}`;
};

const getOccurrenceIso = (event) => {
  if (!Array.isArray(event.incomingOccurrencesInUtc)) {
    return undefined;
  }
  const sorted = [...event.incomingOccurrencesInUtc].sort();
  return sorted[0];
};

export default function OneTime({
  calendar,
  trads,
  langUrl,
  demoMode,
  onEventSave,
  onEventDelete,
}) {
  const tIndex = trads?.index || {};
  const tOneTime = tIndex.oneTimeModal || {};
  const navigatorLocale =
    typeof navigator !== 'undefined' ? navigator.language : 'fr-FR';
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const oneTimeEvents = useMemo(() => {
    const now = new Date();
    return (calendar?.events || [])
      .filter((event) => {
        const isOneTimeByType = event.type === 'oneTime';
        const hasNoRecurringPattern =
          (!event.days || event.days.length === 0) &&
          (!event.daysInMonth || event.daysInMonth.length === 0) &&
          event.everyDays == null;
        if (!isOneTimeByType && !hasNoRecurringPattern) {
          return false;
        }
        const occurrence = getOccurrenceIso(event);
        if (!occurrence) {
          return false;
        }
        return new Date(occurrence) > now;
      })
      .sort((a, b) => {
        const aIso = getOccurrenceIso(a) || '';
        const bIso = getOccurrenceIso(b) || '';
        return aIso.localeCompare(bIso);
      })
      .slice(0, 20);
  }, [calendar]);

  const openCreateModal = () => {
    setSelectedEvent(null);
    setIsModalOpen(true);
  };

  const openUpdateModal = (event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setSelectedEvent(null);
    setIsModalOpen(false);
  };

  return (
    <div className="mb-8">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">
          {tIndex.oneTimeMessageTitle || 'Send or plan one message'}
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          {tIndex.oneTimeMessageDescription ||
            'Send now or schedule one precise SMS delivery.'}
        </p>
      </div>

      <button
        type="button"
        onClick={openCreateModal}
        data-testid="one-time-open-modal-btn"
        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {tIndex.oneTimeCta || 'Send or plan a message'}
      </button>

      <div className="mt-6">
        <p className="text-sm font-medium text-gray-700 mb-2">
          {tIndex.oneTimePlannedTitle || 'One-time events already planned'}
        </p>

        {oneTimeEvents.length === 0 ? (
          <p className="text-sm text-gray-500">
            {tIndex.oneTimeNoEvents || 'No incoming one-time events.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-auto w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-2 pr-4 font-semibold text-gray-700">
                    {tOneTime.date || 'Date'}
                  </th>
                  <th className="py-2 pr-4 font-semibold text-gray-700">
                    {tOneTime.contacts || 'Contacts'}
                  </th>
                  <th className="py-2 pr-4 font-semibold text-gray-700">
                    {tOneTime.smsContent || 'SMS text'}
                  </th>
                  <th className="py-2 font-semibold text-gray-700">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {oneTimeEvents.map((event) => {
                  const occurrence = getOccurrenceIso(event);
                  return (
                    <tr
                      key={event.id}
                      className="border-b border-gray-100 last:border-b-0"
                    >
                      <td className="py-2 pr-4 whitespace-nowrap text-gray-700">
                        {occurrence
                          ? new Date(occurrence).toLocaleString(navigatorLocale)
                          : '-'}
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap text-gray-700">
                        {formatContacts(event)}
                      </td>
                      <td className="py-2 pr-4 text-gray-700 break-words">
                        {event.content}
                      </td>
                      <td className="py-2 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => openUpdateModal(event)}
                          className="text-sm font-medium text-gray-900 underline hover:text-gray-700"
                        >
                          {tOneTime.update || 'Update'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <OneTimeEventModal
        event={selectedEvent}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={onEventSave}
        onDelete={onEventDelete}
        trads={trads}
        langUrl={langUrl}
        demoMode={demoMode}
      />
    </div>
  );
}
